// src/components/Layout.tsx
import React, { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Nav,
  INavLinkGroup,
  INavStyles,
  Text,
  IconButton,
  INavLink,
} from "@fluentui/react";
import { useUser } from "../utils/user-context";
import "./../assets/styles/app.css";
import "./../assets/styles/layout.css";
import { common } from "../messages/common-items";

const { Labels: Lbl_Common, SuccessMessages: Success_Common} = common;

// Define all possible navigation links
const fullNavLinks = [
  { name: Lbl_Common .ENTRANCE_AND_EXIT_MANAGEMENT, url: "", key: "entranceExit" },
  { name: Lbl_Common .WORKER_lIST_BY_SUBCONTRACTOR, url: "/list", key: "list" },
  { name: Lbl_Common .KY_ACTIVITY, url: "/kyactivitylist", key: "kyactivitylist" },
  { name: Lbl_Common .SAFETY_LOG, url: "", key: "safetyLog" },
  { name: Lbl_Common .ONSITE, url: "", key: "onsite" },
  { name: Lbl_Common .PARTNER, url: "", key: "partner" },
  { name: Lbl_Common .ONSITE_PARTNER_RELATION, url: "", key: "onsitePartnerRelation" },
  { name: Lbl_Common .EMPLOYEE, url: "", key: "employee" },
  { name: Lbl_Common .EMPLOYEE_QUALIFICATION, url: "", key: "employeeQualification" },
  { name: Lbl_Common .EMPLOYEE_ACHIEVEMENT, url: "", key: "employeeAchievement" },
  { name: Lbl_Common .CLIENT, url: "", key: "client" },
  { name: Lbl_Common .RISKY_WORK, url: "", key: "riskyWork" },
  { name: Lbl_Common .RISK_AVOIDANCE, url: "", key: "riskAvoidance" },
];

// Role-based access (using normalized roles)
const roleAccess: Record<string, string[]> = {
  admin: [
    "entranceExit",
    "list",
    "kyactivitylist",
    "safetyLog",
    "onsite",
    "partner",
    "onsitePartnerRelation",
    "employee",
    "employeeQualification",
    "employeeAchievement",
    "client",
    "riskyWork",
    "riskAvoidance",
  ],
  user: [
    "entranceExit",
    "list",
    "kyactivitylist",
    "safetyLog",
    "onsitePartnerRelation",
    "riskyWork",
    "riskAvoidance",
  ],
  guest: [],
};

const navStyles: Partial<INavStyles> = {
  root: { className: "customNav" },
};

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useUser();

  // Filter allowed links based on current role
  const allowedKeys = role ? roleAccess[role] ?? [] : [];
  const filteredLinks = fullNavLinks.filter((link) =>
    allowedKeys.includes(link.key)
  );

  const navLinkGroups: INavLinkGroup[] = [{ links: filteredLinks }];

  // Handle navigation click
  const onLinkClick = useCallback(
    (ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
      ev?.preventDefault();
      if (item?.url) {
        navigate(item.url);
        if (isMobile) setMenuOpen(false);
      }
    },
    [navigate]
  );

  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [screenReady, setScreenReady] = useState(false);

  // Utility to check if screen is small
  const checkIsMobile = () => window.innerWidth < 768;

  // Initialize layout on mount
  useLayoutEffect(() => {
    const small = checkIsMobile();
    setIsMobile(small);
    setCollapsed(small);
    setScreenReady(true);
  }, []);

  // Update layout on resize
  useEffect(() => {
    const handleResize = () => {
      const small = checkIsMobile();
      setIsMobile(small);
      setCollapsed(small);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!screenReady) return null;

  return (
    <div className="app-layout">
      {/* Header */}
      <div className={`header ${isMobile ? "header-mobile" : "header-desktop"}`}>
        {isMobile && (
          <div className="mobile-menu-icon">
            <IconButton
              iconProps={{ iconName: "GlobalNavButton" }}
              onClick={() => setMenuOpen(true)}
              styles={{ root: { color: "white" } }}
            />
          </div>
        )}
        <h3>{Success_Common.HEADER_TEXT}</h3>
      </div>

      {/* Body */}
      <div style={{ display: "flex", flexGrow: 1 }}>
        {/* Sidebar for Desktop */}
        {!isMobile && filteredLinks.length > 0 && (
          <div
            className={`sidebar ${
              collapsed ? "sidebar-collapsed" : "sidebar-expanded"
            }`}
          >
            <IconButton
              iconProps={{ iconName: collapsed ? "GlobalNavButton" : "ChevronLeft" }}
              title="Toggle Menu"
              ariaLabel="Toggle Menu"
              onClick={() => setCollapsed(!collapsed)}
              styles={{ root: { margin: 10 } }}
            />
            {!collapsed && (
              <Nav
                selectedKey={location.pathname.split("/")[1]}
                onLinkClick={onLinkClick}
                groups={navLinkGroups}
                styles={navStyles}
              />
            )}
          </div>
        )}

        {/* Main Content */}
        <div
          className={`content ${
            !isMobile
              ? collapsed
                ? "content-sidebar-collapsed"
                : "content-sidebar-expanded"
              : ""
          }`}
        >
          <Outlet />
        </div>
      </div>

      {/* Footer */}
      <div className="footer">
        <Text variant="small">{Success_Common.FOOTER_TEXT}</Text>
      </div>

      {/* Mobile Menu */}
      {isMobile && menuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu-header">
            <h3 style={{ margin: 0 }}>{Lbl_Common .MENU_LBL}</h3>
            <IconButton
              iconProps={{ iconName: "ChromeClose" }}
              onClick={() => setMenuOpen(false)}
              styles={{ root: { color: "white" } }}
            />
          </div>
          <div className="mobile-menu-body">
            <Nav
              selectedKey={location.pathname.split("/")[1]}
              onLinkClick={onLinkClick}
              groups={navLinkGroups}
              styles={navStyles}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;